import React, { useState, useEffect } from "react";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Flex,
  Divider,
  Input,
  TextArea
} from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import { usePlanManagerContext, ReviewMode } from "@/providers/PlanManagerProvider";
import { appstr } from "@coreapp/utils/appstr";
import { usePlanSubmission } from "@/hooks/usePlanSubmission";
import { ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import {
  PlanDetailsGrid,
  PlanInfoSection,
  ReviewersSection,
  SubmissionInfoSection
} from "../PlanSubmissionDetails/PlanSubmissionDetails";
import PlanDetailsSummaryCard from "@/appComponents/Blocks/Composites/PlanDetailsSummaryCard/PlanDetailsSummaryCard";
import { useSubmissionVisibility } from "@/hooks/useSubmissionVisibility";

export const PlanReviewModal = () => {
  const {
    planReviewContextValue: { showReviewModal, reviewMode, closeReviewModal }
  } = usePlanManagerContext();

  const { approvePlanSubmission, rejectPlanSubmission } = usePlanSubmission();

  const [feedback, setFeedback] = useState("");

  const { planManager } = usePlanManagerContext();
  const planSubmission = planManager?.state?.GetPlanSubmission();

  const { getCurrentUserReviewStatus } = useSubmissionVisibility(planSubmission);

  useEffect(() => {
    if (!showReviewModal) {
      setFeedback("");
    }
  }, [showReviewModal]);

  if (!showReviewModal || !planSubmission) return null;

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.fPlanManager;
  const planInfo = planMgr?.GetPlanInfo();

  const isApproveMode = reviewMode === ReviewMode.APPROVE;
  const currentUserStatus = getCurrentUserReviewStatus();
  const isReviewDisabled = isApproveMode
    ? currentUserStatus === ReviewerStatus.Approved
    : currentUserStatus === ReviewerStatus.Rejected;

  const handleAction = () => {
    if (isApproveMode) {
      approvePlanSubmission(feedback);
    } else {
      rejectPlanSubmission(feedback);
    }
    closeReviewModal();
  };

  const modalTitle = isApproveMode ? appstr("Approve plan") : appstr("Reject plan");
  const feedbackLabel = isApproveMode ? appstr("Approval message") : appstr("Rejection message");
  const actionButtonLabel = isApproveMode ? appstr("Approve plan") : appstr("Reject plan");

  return (
    <CentralizedModalContainer show={showReviewModal}>
      <Modal
        id="plan-review-modal"
        show={showReviewModal}
        onClose={closeReviewModal}
        minWidth={"medium"}
        maxWidth={"medium"}
        withShadow={true}
        centerScreen={true}
      >
        <DialogTitle divider={true} onClose={closeReviewModal}>
          {modalTitle}
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content"}>
          <Flex direction={"column"} gap={"16px"} height={"100%"}>
            <PlanDetailsSummaryCard>
              <PlanDetailsGrid>
                <PlanInfoSection planInfo={planInfo} />
                <SubmissionInfoSection submission={planSubmission.submission} />
              </PlanDetailsGrid>
            </PlanDetailsSummaryCard>
            <ReviewersSection reviewers={planSubmission.reviewers} />

            <Divider />
            <TextArea
              id="plan-review-feedback"
              dataTestId={isApproveMode ? "plan-approve-message" : "plan-reject-message"}
              placeholder={appstr("Add a message")}
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              rows={4}
              charLimit={1000}
              label={feedbackLabel}
            />
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary onClick={closeReviewModal} data-testid="plan-review-cancel-btn">
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleAction}
            disabled={isReviewDisabled}
            data-testid={isApproveMode ? "plan-approve-btn" : "plan-reject-btn"}
          >
            {actionButtonLabel}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
